/**
 * useFlightStatus — live flight status for a transit item.
 *
 * Fetches `/arches/travel/items/:id/flight-status` and adapts the refetch
 * interval to how close the flight is in time:
 *   - in-flight (departed but not arrived): every 60 seconds
 *   - scheduled within 2h of departure: every 60 seconds
 *   - scheduled within 24h of departure: every 5 minutes
 *   - otherwise: no automatic refetch (the user can trigger manually)
 *
 * When the server reports a new `status` that differs from the prior cached
 * one, a toast fires — this is the only "notification" path today because
 * the project has no cross-channel (push / email) mechanism for background
 * events. The worker-side poller also logs status changes; future work is
 * to fan those out to browser push + SES email.
 *
 * The hook is a no-op (enabled: false) when:
 *   - no itemId is provided
 *   - the item is not a Flight transit
 *   - the `flight_status` feature is disabled
 */

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { travelApi } from '../api';
import type {
  TravelItemResponse,
  TravelFlightStatusFetchResponse,
  TravelFlightStatusValue,
} from '../api';
import { queryKeys } from '@/lib/query-keys';
import { useTravelFeature } from './useTravelUserProfile';
import { toast } from '@/components/ui/use-toast';

export interface FlightStatusSnapshot {
  status: TravelFlightStatusValue | null;
  airline: string | null;
  flightNumber: string | null;
  departureGate: string | null;
  arrivalGate: string | null;
  scheduledDeparture: string | null;
  actualDeparture: string | null;
  scheduledArrival: string | null;
  actualArrival: string | null;
  lastPolledAt: string | null;
  lastStatusChangeAt: string | null;
  providerError?: string;
  providerMiss?: boolean;
  providerUnavailable?: boolean;
}

/**
 * Picks the refetch interval (ms) based on how close the flight is to
 * departure, or `false` if no polling is warranted. The "in flight" case
 * (actualDeparture set, actualArrival not) wins even if scheduledDeparture
 * is in the past.
 */
export function computeFlightRefetchInterval(
  status: TravelFlightStatusValue | null,
  scheduledDeparture: string | null,
  actualDeparture: string | null,
  actualArrival: string | null,
  now: Date = new Date(),
): number | false {
  if (status === 'landed' || status === 'cancelled') return false;

  // In-flight: departed but not arrived — tightest cadence.
  if (actualDeparture && !actualArrival) return 60_000;

  if (!scheduledDeparture) return false;
  const depMs = new Date(scheduledDeparture).getTime();
  if (Number.isNaN(depMs)) return false;
  const diffHours = (depMs - now.getTime()) / (1000 * 60 * 60);

  // 1 hour before through 2 hours after scheduled departure → 60 seconds.
  // diffHours = (depTime - now) / hour, so "before departure" is positive and
  // "after departure" is negative.
  if (diffHours >= -2 && diffHours <= 1) return 60_000;
  // Within 24 hours of departure → 5 minutes.
  if (diffHours > 1 && diffHours <= 24) return 5 * 60_000;
  return false;
}

function describeStatus(value: TravelFlightStatusValue): string {
  switch (value) {
    case 'scheduled':
      return 'Flight is scheduled';
    case 'active':
      return 'Flight is in the air';
    case 'landed':
      return 'Flight has landed';
    case 'delayed':
      return 'Flight is delayed';
    case 'cancelled':
      return 'Flight has been cancelled';
    case 'diverted':
      return 'Flight has been diverted';
    default:
      return 'Flight status updated';
  }
}

export function useFlightStatus(item: TravelItemResponse | null | undefined) {
  const feature = useTravelFeature('flight_status');
  const isFlight = item?.itemType === 'transit' && item.transitMode === 'Flight';
  const enabled = !!item && isFlight && feature.enabled;
  const lastSeenStatusRef = useRef<TravelFlightStatusValue | null>(null);

  const query = useQuery<TravelFlightStatusFetchResponse>({
    queryKey: item
      ? queryKeys.travel.flightStatus(item.itemId)
      : ['travel', 'flightStatus', 'none'],
    enabled,
    queryFn: () => travelApi.getTravelFlightStatus(item!.itemId),
    refetchInterval: (q) => {
      const data = q.state.data as TravelFlightStatusFetchResponse | undefined;
      const s = data?.status;
      return computeFlightRefetchInterval(
        s?.status ?? null,
        s?.scheduledDeparture ?? null,
        s?.actualDeparture ?? null,
        s?.actualArrival ?? null,
      );
    },
    refetchIntervalInBackground: false,
  });

  // Detect status changes and fire a toast. We compare against the previous
  // render's status rather than the server's `lastStatusChangeAt` because
  // the UI cares about "did the status change since I was last looking".
  useEffect(() => {
    const s = query.data?.status?.status ?? null;
    const prior = lastSeenStatusRef.current;
    if (!s) return;
    if (prior !== null && prior !== s) {
      const title =
        s === 'cancelled'
          ? 'Flight cancelled'
          : s === 'delayed'
            ? 'Flight delayed'
            : s === 'diverted'
              ? 'Flight diverted'
              : describeStatus(s);
      const fn = query.data?.status?.flightNumber;
      toast({
        title,
        description: fn ? `${fn}: ${describeStatus(s)}` : describeStatus(s),
        variant: s === 'cancelled' || s === 'diverted' ? 'destructive' : 'default',
      });
    }
    lastSeenStatusRef.current = s;
  }, [query.data?.status?.status, query.data?.status?.flightNumber]);

  const snapshot: FlightStatusSnapshot | null = query.data?.status
    ? {
        status: query.data.status.status,
        airline: query.data.status.airline,
        flightNumber: query.data.status.flightNumber,
        departureGate: query.data.status.departureGate,
        arrivalGate: query.data.status.arrivalGate,
        scheduledDeparture: query.data.status.scheduledDeparture,
        actualDeparture: query.data.status.actualDeparture,
        scheduledArrival: query.data.status.scheduledArrival,
        actualArrival: query.data.status.actualArrival,
        lastPolledAt: query.data.status.lastPolledAt,
        lastStatusChangeAt: query.data.status.lastStatusChangeAt,
        providerError: query.data.providerError,
        providerMiss: query.data.providerMiss,
        providerUnavailable: query.data.providerUnavailable,
      }
    : null;

  return {
    snapshot,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    refresh: () => query.refetch(),
    featureEnabled: feature.enabled,
    isFlight,
  };
}
